import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { fetchWebinars, deleteWebinar } from '../../api/webinars';
import ShopmyImage from '../General/ShopmyImage';
import WebinarEditor from './WebinarEditor';
import './Webinars.scss';

const NEW_ID = 'new';

const formatEasternTime = raw =>
  raw
    ? `${new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }).format(new Date(raw))} ET`
    : 'No date';

// Local mirror of getWebinarCardState in @shopmy/universal — admin has no package dep.
// Undated drafts stay on Upcoming so a new session isn't buried in Past before a date is set.
const getDerivedState = webinar => {
  const startsAt = webinar.startsAt && new Date(webinar.startsAt);
  if (!startsAt) return 'upcoming';
  const endsAt = webinar.endsAt ? new Date(webinar.endsAt) : new Date(startsAt.getTime() + 60 * 60 * 1000);
  const now = new Date();
  if (now < startsAt) return 'upcoming';
  if (now <= endsAt) return 'live';
  if (!webinar.replayUrl) return 'awaiting_replay';
  return 'past';
};

const DERIVED_STATE_LABELS = {
  upcoming: 'Upcoming',
  live: 'Live now',
  awaiting_replay: 'Needs recording',
  past: 'Past'
};

const belongsOnUpcomingTab = webinar => {
  const state = getDerivedState(webinar);
  return state === 'upcoming' || state === 'live';
};

const TABS = [
  { id: 'upcoming', label: 'Upcoming Webinars' },
  { id: 'past', label: 'Past Webinars' }
];

const WebinarRowContent = ({ webinar }) => {
  const derivedState = getDerivedState(webinar);
  return (
    <>
      {webinar.backgroundImage || webinar.heroImage ? (
        <ShopmyImage source={webinar.backgroundImage || webinar.heroImage} alt={webinar.title} className='wb-row-image' />
      ) : (
        <div className='wb-row-image wb-row-image--empty'>No image</div>
      )}
      <div className='wb-row-body'>
        <div className='wb-row-title'>{webinar.title || 'Untitled webinar'}</div>
        <div className='wb-row-date'>{formatEasternTime(webinar.startsAt)}</div>
      </div>
      <div className='wb-row-meta'>
        <span className={`wb-status-badge wb-status-badge--${webinar.status}`}>{webinar.status}</span>
        <span
          className={`wb-state-chip wb-state-chip--${derivedState}`}
          title={derivedState === 'awaiting_replay' ? 'Hidden from the website until a recording link is added.' : undefined}
        >
          {DERIVED_STATE_LABELS[derivedState]}
        </span>
        {webinar.topic && <span className='wb-topic-badge'>{webinar.topic}</span>}
      </div>
    </>
  );
};

const WebinarRow = ({ webinar, isArchived, isSelected, onSelect, children }) => (
  <div className={cn('wb-row', { 'wb-row--archived': isArchived, 'wb-row--selected': isSelected })}>
    <div className='wb-row-summary' onClick={() => onSelect(webinar)}>
      <WebinarRowContent webinar={webinar} />
    </div>
    {isSelected && children}
  </div>
);

const Webinars = () => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('upcoming');
  const [selectedId, setSelectedId] = useState(null);
  const [confirmDeleteWebinar, setConfirmDeleteWebinar] = useState(null);

  const isUpcomingTab = tab === 'upcoming';
  // Mirrors the public page: upcoming soonest-first, past newest-first. No manual ordering.
  const byDate = (a, b) => (isUpcomingTab ? 1 : -1) * String(a.startsAt || '9999').localeCompare(String(b.startsAt || '9999'));
  const tabWebinars = webinars.filter(w => (isUpcomingTab ? belongsOnUpcomingTab(w) : !belongsOnUpcomingTab(w))).sort(byDate);
  const visibleWebinars = tabWebinars.filter(w => w.status !== 'archived');
  const archivedWebinars = tabWebinars.filter(w => w.status === 'archived');
  const isAdding = selectedId === NEW_ID;

  const loadWebinars = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWebinars();
      setWebinars(data || []);
    } catch {
      setError('Failed to load webinars.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebinars();
  }, []);

  const handleTabChange = nextTab => {
    setTab(nextTab);
    setSelectedId(null);
  };

  const handleSelect = webinar => {
    setSelectedId(prev => (prev === webinar.id ? null : webinar.id));
  };

  const handleSave = savedWebinar => {
    setWebinars(prev => {
      const idx = prev.findIndex(w => w.id === savedWebinar.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = savedWebinar;
        return updated;
      }
      return [savedWebinar, ...prev];
    });
    setTab(belongsOnUpcomingTab(savedWebinar) ? 'upcoming' : 'past');
    setSelectedId(savedWebinar.id);
  };

  const executeDelete = async () => {
    const webinar = confirmDeleteWebinar;
    setConfirmDeleteWebinar(null);
    try {
      await deleteWebinar(webinar.id);
      setWebinars(prev => prev.filter(w => w.id !== webinar.id));
      setSelectedId(prev => (prev === webinar.id ? null : prev));
    } catch {
      window.ALERT?.error('Failed to delete webinar.');
    }
  };

  return (
    <div className='webinars-outer'>
      <div className='webinars-inner'>
        <div className='wb-header'>
          <div>
            <h1 className='wb-title'>Webinars</h1>
          </div>
        </div>

        <div className='wb-tabs'>
          {TABS.map(({ id, label }) => (
            <button key={id} type='button' className={cn('wb-tab', { 'wb-tab--active': tab === id })} onClick={() => handleTabChange(id)}>
              {label}
            </button>
          ))}
        </div>

        <div className='wb-toolbar'>
          <div className='wb-order-note'>
            {isUpcomingTab
              ? 'Sessions appear on the website in chronological order — the next session first. To remove one, change its Visibility.'
              : 'Replays appear on the website newest-first. To remove one, change its Visibility.'}
          </div>
          <button
            type='button'
            className='wb-new-btn'
            onClick={() => setSelectedId(NEW_ID)}
            disabled={isAdding}
          >
            {isUpcomingTab ? '+ Add an Upcoming Webinar' : '+ Add a Past Webinar'}
          </button>
        </div>

        {loading && <div className='wb-loading'>Loading...</div>}
        {error && <div className='wb-error'>{error}</div>}

        {!loading && !error && (
          <>
            {isAdding && (
              <div className='wb-row wb-row--selected wb-row--new'>
                <div className='wb-row-summary wb-row-summary--static'>
                  <div className='wb-row-image wb-row-image--empty'>New</div>
                  <div className='wb-row-body'>
                    <div className='wb-row-title'>{isUpcomingTab ? 'New upcoming webinar' : 'New past webinar'}</div>
                    <div className='wb-row-date'>Fill in the details below, then save as draft or publish.</div>
                  </div>
                </div>
                <WebinarEditor
                  key={NEW_ID}
                  webinar={null}
                  mode={tab}
                  onSave={handleSave}
                  onCancel={() => setSelectedId(null)}
                />
              </div>
            )}

            {visibleWebinars.map(webinar => (
              <WebinarRow key={webinar.id} webinar={webinar} isSelected={selectedId === webinar.id} onSelect={handleSelect}>
                <WebinarEditor
                  key={webinar.id}
                  webinar={webinar}
                  mode={tab}
                  onSave={handleSave}
                  onCancel={() => setSelectedId(null)}
                  onDelete={setConfirmDeleteWebinar}
                />
              </WebinarRow>
            ))}

            {!visibleWebinars.length && !isAdding && (
              <div className='wb-empty'>
                {isUpcomingTab
                  ? 'No upcoming webinars on the hub yet — add one to get started.'
                  : 'No past webinars in the library yet — add a replay card whenever you are ready.'}
              </div>
            )}

            {!!archivedWebinars.length && (
              <div className='wb-archived-section'>
                <div className='wb-archived-title'>Archived</div>
                <div className='wb-archived-helper'>
                  Not shown on the public page. Open one and set Visibility back to Published to restore it.
                </div>
                {archivedWebinars.map(webinar => (
                  <WebinarRow key={webinar.id} webinar={webinar} isArchived isSelected={selectedId === webinar.id} onSelect={handleSelect}>
                    <WebinarEditor
                      key={webinar.id}
                      webinar={webinar}
                      mode={tab}
                      onSave={handleSave}
                      onCancel={() => setSelectedId(null)}
                      onDelete={setConfirmDeleteWebinar}
                    />
                  </WebinarRow>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {confirmDeleteWebinar && (
        <div className='wb-confirm-backdrop' onClick={() => setConfirmDeleteWebinar(null)}>
          <div className='wb-confirm-modal' onClick={e => e.stopPropagation()}>
            <div className='wb-confirm-title'>Delete webinar?</div>
            <div className='wb-confirm-body'>
              "{confirmDeleteWebinar.title}" will be permanently removed and cannot be recovered.
            </div>
            <div className='wb-confirm-actions'>
              <button className='wb-confirm-btn wb-confirm-btn--cancel' onClick={() => setConfirmDeleteWebinar(null)}>
                Cancel
              </button>
              <button className='wb-confirm-btn wb-confirm-btn--delete' onClick={executeDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Webinars;
